import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { notifyEmber } from 'utils/ember-communication';
import transferLocalStorageDataToCookie from 'utils/cookies/transfer-local-storage-data-to-cookie';
import endpoint, { ErrorResponse } from 'utils/endpoint';
import useAuthenticatedRoute from 'hooks/use-authenticated-route';
import { DEFAULT_EXPIRE_DAYS, ENABLE_AI_ALPHA_CHAT_COOKIE_NAME, JWT_COOKIE_NAME, LOCATION_EXPIRE_DAYS, SITE_LOCATION_COOKIE_NAME, USER_COOKIE_NAME, USER_EXPIRE_DAYS, USER_LOCATION_COOKIE_NAME } from 'constants/cookies';
import { CountryCodeList } from 'types/countries';
import { defaultCACityPayload, defaultUSCityPayload } from 'constants/locations';
import {
  acceptProviderTermsForUser,
  getAcceptedTermsByUserFromCookies,
  checkZoocasaTermsAreReadInCookies,
  setZoocasaTermsAreReadInCookies,
} from 'utils/compliance';
import { Provider } from '@zoocasa/go-search';
import { HttpRequestMethodTypes } from 'types';
import { useFeaturesContext } from './features';
import { dasherizeKeys } from '@zoocasa/node-kit/objects';
import { camelize } from '@zoocasa/node-kit/strings/camelize';
import { useThemeContext, useModalContext } from './';
import { handleQuebecPopup, IsUserInQuebec } from 'utils/modals';
import useDebounce from 'hooks/use-debounce';
import { useTranslation } from 'next-i18next';
import { LOCALE_FORM_ERROR_PATHS, Namespaces } from 'types/locales';
import { withTryAsync } from 'utils/withTry';
import { themeNameToTenantId } from '@tenants';
import jwt from 'jsonwebtoken';
import { IncomingMessage } from 'http';
import { getJSON, getObjectFromRequestCookie, getStringFromRequestCookie } from 'utils/cookies';
import { ObservabilityController } from 'observability';
import { loadConversationalSearchData } from 'utils/conversational-search/load-search-data';

import type { IUserLocation } from 'utils/user-location';
import type { CountryCode } from 'types/countries';
import type { SearchSuggestions } from 'components/suggested-location-dropdown';
import type { SearchParameters } from 'data/conversational-search/types';

export type User = {
  id: string | number;
  firstName: string;
  lastName: string;
  homePhone: string;
  email: string;
  jwt: string;
  newsletter: boolean;
  ssoProvider: string | null;
  hasPassword: boolean;
  hideImagelessListings: boolean;
  locale: string;
};

export interface ValidateUserRequest {
  email?: string;
  userId?: string;
  provider?: string;
}

export interface ValidateUserTheme {
  user_id: number;
  acceptance: string;
}

export interface ValidateUserResponse {
  auth: string[];
  firstName: string;
  theme: {
    [themeName: string]: ValidateUserTheme;
  };
}

export interface AuthErrorResponse {
  error: string;
  message: string;
}

type JSONAPIPayload = {
  data: {
    id: string | number;
    attributes: {
      firstName: string;
      lastName: string;
      homePhone: string;
      email: string;
      jwt: string;
      newsletter: boolean;
      ssoProvider: string | null;
      hasPassword: boolean;
      hideImagelessListings: boolean;
      locale: string;
    };
  };
};

type UserRegisterResponse = {
  data: Record<string, unknown>[];
  errors: {
    source?: {
      pointer?: string;
    };
    title?: string;
  }[];
};
type Success = { success: string };
type Error = { error: string };

export interface IUserContext {
  user: User | null;
  isAuthenticated: boolean;
  authenticateUser: (properties: Record<string, unknown>) => Promise<void | Error>;
  registerUser: (properties: Record<string, unknown>) => Promise<void | Error>;
  acceptedBrowserBasedTerms: Provider[];
  acceptedTerms: Provider[];
  acceptTerms: (providerId: Provider) => void;
  setZoocasaTermsStateToRead: () => void;
  areZoocasaTermsRead: boolean;
  confirmUser: (confirmationToken: string, userAgent: string, userLocationSlug: string, userLatitude: number, userLongitude: number) => Promise<Success | Error>;
  signOut: () => Promise<void>;
  recoverPasswordForEmail: (email: string) => Promise<Success | Error>;
  resetPassword: (params: { password: string; passwordConfirmation: string; resetPasswordToken: string }) => Promise<Success | Error>;
  updatePassword: (params: { currentPassword?: string; password: string; passwordConfirmation: string; email?: string }) => Promise<Success | Error>;
  updateUserData: (params: User) => Promise<Success | Error>;
  saveAndPersistUser: (user: User | null) => void;
  validateUser: (user: ValidateUserRequest) => Promise<ValidateUserResponse | AuthErrorResponse>;
  userLocation?: IUserLocation;
  siteLocation?: CountryCode;
  userLocationSameAsSiteLocation: boolean;
  handleSetSiteLocation: (countryCode: CountryCode) => void;
  handleSetDefaultUserLocation: (countryCode: CountryCode) => void;
  hasAcceptedConnRequestDisclaimers: boolean;
  handleConnRequestDisclaimers: (isChecked: { isChecked: boolean }) => void;
  userPopularCities: SearchSuggestions[];
  hasNavigatedFromInternalLink: boolean;
  setHasNavigatedFromInternalLink: (hasNavigatedFromInternalLink: boolean) => void;
  updateEmail: (params: { newEmail: string; retypeEmail: string; password?: string; passwordConfirmation?: string }) => Promise<Success | Error>;
  isOnSignUpUrl: boolean;
  setIsOnSignUpUrl: (isOnSignupUrl: boolean) => void;
  isCrawler: boolean;
  functionalCookiesEnabled: boolean;
  aiSearchParams: SearchParameters | null;
  setAiSearchParams: (params: SearchParameters | null) => void;
}

export const UserContext = createContext<IUserContext | Record<string, unknown>>({});
export function useUserContext <T extends IUserContext | Record<string, unknown> = IUserContext>() {
  return useContext(UserContext) as T;
}

interface Props {
  user: IUserContext['user'];
  userLocation?: IUserLocation;
  storedSiteLocation?: CountryCode;
  storedConnRequestDisclaimers: boolean;
  storedUserPopularCities?: SearchSuggestions[];
  isCrawler: boolean;
  functionalCookiesEnabled?: boolean;
  children: ReactNode;
}


export default function UserContextProvider({ user: userData, userLocation: storedUserLocation, storedSiteLocation, storedConnRequestDisclaimers, storedUserPopularCities, isCrawler, functionalCookiesEnabled, children }: Props) {
  transferLocalStorageDataToCookie(USER_COOKIE_NAME);
  const { features } = useFeaturesContext();
  const { themeName } = useThemeContext();
  const { openModal } = useModalContext();
  const [user, setUser] = useState<IUserContext['user']>(userData as IUserContext['user']);
  const [isAuthenticated, setIsAuthenticated] = useState<IUserContext['isAuthenticated']>(!!(user && user.jwt));
  const [siteLocation, setSiteLocation] = useState<CountryCode | undefined>(storedSiteLocation);
  const [userLocation, setUserLocation] = useState<IUserLocation | undefined>(storedUserLocation);
  const [hasAcceptedConnRequestDisclaimers, setHasAcceptedConnRequestDisclaimers] = useState(storedConnRequestDisclaimers);
  const [hasNavigatedFromInternalLink, setHasNavigatedFromInternalLink] = useState<IUserContext['hasNavigatedFromInternalLink']>(false);
  const [isOnSignUpUrl, setIsOnSignUpUrl] = useState(false);

  const [acceptedBrowserBasedTerms, setAcceptedBrowserBasedTerms] = useState<Provider[]>([]);
  const [acceptedTerms, setAcceptedTerms] = useState<Provider[]>([]);
  const [areZoocasaTermsRead, setAreZoocasaTermsRead] = useState(false);
  const [aiSearchParams, setAiSearchParams] = useState<SearchParameters | null>(null);

  const userPopularCities = storedUserPopularCities || [];
  const userLocationSameAsSiteLocation = userLocation?.countryCode === siteLocation;
  const { t } = useTranslation(Namespaces.FORM_ERROR);
  useAuthenticatedRoute(isAuthenticated);

  useEffect(() => {
    if (!siteLocation) {
      if (!features?.useUsListings) {
        handleSetSiteLocation(CountryCodeList.CANADA);
      } else {
        if (userLocation?.countryCode === CountryCodeList.UNITED_STATES) {
          handleSetSiteLocation(CountryCodeList.UNITED_STATES);
        } else if (userLocation?.countryCode) {
          handleSetSiteLocation(CountryCodeList.CANADA);
        }
      }
    }
  }, [userLocation, siteLocation, features?.useUsListings]);

  const isSupportedCountryCode = useCallback((countryCode = CountryCodeList.CANADA) => {
    return countryCode === CountryCodeList.CANADA || countryCode === CountryCodeList.UNITED_STATES;
  }, []);

  useEffect(() => {
    // Only use location from cookies if allowed and location country supported by the site
    if (functionalCookiesEnabled && storedUserLocation && isSupportedCountryCode(storedUserLocation?.countryCode)) {
      Cookies.set(USER_LOCATION_COOKIE_NAME, JSON.stringify(storedUserLocation), { expires: DEFAULT_EXPIRE_DAYS });
      setUserLocation(storedUserLocation);
    }
    else if (siteLocation === CountryCodeList.UNITED_STATES) {
      setUserLocation(defaultUSCityPayload);
    }
    else {
      setUserLocation(defaultCACityPayload);
    }
  }, [storedUserLocation, functionalCookiesEnabled, siteLocation]);

  useEffect(() => {
    if (storedSiteLocation) {
      handleSetSiteLocation(storedSiteLocation);
    }
  }, [storedSiteLocation]);

  useEffect(() => {
    setIsAuthenticated(!!(user && user.jwt));
    checkZoocasaTermsState();
    checkAllAcceptedTerms();
    notifyEmber({ type: 'authentication' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Load AI search params when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && user?.jwt && (features?.useAiSearch || Cookies.get(ENABLE_AI_ALPHA_CHAT_COOKIE_NAME) === 'true')) {
      loadConversationalSearchData({
        jwt: user.jwt,
        tenantId: themeNameToTenantId(themeName),
      })
        .then(params => {
          if (params && Object.keys(params).length > 0) {
            setAiSearchParams(params);
          } else {
            setAiSearchParams({});
          }
        })
        .catch(error => {
          console.error('Failed to load AI search params:', error);
        });
    } else if (!isAuthenticated) {
      // Clear AI search params when user logs out
      setAiSearchParams(null);
    }
  }, [isAuthenticated, user?.jwt, features?.useAiSearch, themeName]);

  const debouncedHandleQuebecPopup = useDebounce(() => {
    const isUserInQuebec = IsUserInQuebec(userLocation);
    handleQuebecPopup({ tenantId: themeNameToTenantId(themeName), isQuebec: isUserInQuebec, openModal, source: 'homepage' });
  }, 300);

  useEffect(() => {
    // Handles Quebec popup on user's location
    debouncedHandleQuebecPopup();
  }, [themeName, userLocation, openModal, debouncedHandleQuebecPopup]);

  const authenticateUser: IUserContext['authenticateUser'] = async properties => {
    const observabilityContext = {
      authMethod: 'email' as const,
      tenantId: themeNameToTenantId(themeName),
    };
    
    const sessionId = ObservabilityController.trackAuthSigninAttempt(observabilityContext);

    try {
      const response = await endpoint<JSONAPIPayload>('/services/api/v4/auth/login', HttpRequestMethodTypes.POST, properties);
      if (response.data.attributes) {
        setUserFromResponse(response);
        Cookies.set(JWT_COOKIE_NAME, response.data.attributes.jwt, { expires: USER_EXPIRE_DAYS });
        
        ObservabilityController.trackAuthSigninSuccess({
          ...observabilityContext,
          userId: response.data.id.toString(),
          sessionId,
        });
      } else {
        ObservabilityController.trackAuthSigninFailure({
          ...observabilityContext,
          error: { message: 'Invalid credentials' },
          errorCategory: 'invalid_credentials',
          sessionId,
        });
        return { error: t(LOCALE_FORM_ERROR_PATHS.INVALID_PASSWORD, { defaultValue: 'Invalid Email / Password combination.' }) };
      }
    } catch (error) {
      ObservabilityController.trackAuthSigninFailure({
        ...observabilityContext,
        error,
        errorCategory: 'unknown_error',
        sessionId,
      });
      return { error: t(LOCALE_FORM_ERROR_PATHS.INVALID_PASSWORD, { defaultValue: 'Invalid Email / Password combination.' }) };
    }
  };

  const registerUser: IUserContext['registerUser'] = async properties => {
    const [response, error] = await withTryAsync<UserRegisterResponse, ErrorResponse>(endpoint<UserRegisterResponse>)('/services/api/v4/auth', HttpRequestMethodTypes.POST, {
      data: {
        type: 'users',
        attributes: dasherizeKeys(properties),
      },
    });

    if (error) {
      if ('errors' in error && error.errors.length) {
        throw {
          errors: response.errors.map(({ source, title }) => {
            return {
              attribute: camelize((source?.pointer || '').replace('/data/attributes/', '')),
              message: title,
            };
          }),
        };
      }
    } else if ('error' in response) {
      throw {
        errors: [{
          attribute: 'other',
          message: t(LOCALE_FORM_ERROR_PATHS.OTHER, { defaultValue: 'Looks like something went wrong, please try again.' }),
        }],
      };
    } else {
      await authenticateUser({ email: properties.email, password: properties.password });
    }
  };

  const checkAllAcceptedTerms = () => { // Update all states
    const { userBasedTerms, browserBasedTerms } = getAcceptedTermsByUserFromCookies(user?.id);
    setAcceptedTerms(userBasedTerms);
    setAcceptedBrowserBasedTerms(browserBasedTerms);
  };

  const acceptTerms = (providerId: Provider) => {
    acceptProviderTermsForUser(providerId, user?.id);
    checkAllAcceptedTerms();
  };

  const setZoocasaTermsStateToRead = () => {
    setZoocasaTermsAreReadInCookies();
    checkZoocasaTermsState();
  };

  const checkZoocasaTermsState = () => {
    setAreZoocasaTermsRead(checkZoocasaTermsAreReadInCookies());
  };

  const validateUser = async (user: ValidateUserRequest): Promise<ValidateUserResponse | AuthErrorResponse> => {
    try {
      const response = await endpoint<ValidateUserResponse | AuthErrorResponse | ErrorResponse>('/services/api/v4/auth/next_steps', HttpRequestMethodTypes.GET, user as Record<string, unknown>);

      if ('errors' in response) {
        return { 
          error: t(LOCALE_FORM_ERROR_PATHS.VALIDATION_REQUEST_FAILED, { defaultValue: 'Request failed' }), 
          message: t(LOCALE_FORM_ERROR_PATHS.VALIDATION_ERROR_MESSAGE, { defaultValue: 'An error occurred while validating the user' }),
        };
      } else if ('error' in response) {
        return response;
      } else {
        return response;
      }
    } catch (error) {
      console.error('An error occurred while validating the user:', error);
      return { 
        error: t(LOCALE_FORM_ERROR_PATHS.VALIDATION_REQUEST_FAILED, { defaultValue: 'Request failed' }), 
        message: t(LOCALE_FORM_ERROR_PATHS.VALIDATION_ERROR_MESSAGE, { defaultValue: 'An error occurred while validating the user' }),
      };
    }
  };

  const confirmUser: IUserContext['confirmUser'] = async (confirmationToken, userAgent, userLocationSlug, userLatitude, userLongitude) => {
    const params = {
      confirmationToken,
      userAgent,
      userLocationSlug,
      userLatitude,
      userLongitude,
      theme: themeName,
    };
    try {
      const response = await endpoint<JSONAPIPayload>('/services/api/v3/users/confirmation', HttpRequestMethodTypes.GET, params);
      setUserFromResponse(response);
      const successMessage = t(LOCALE_FORM_ERROR_PATHS.ACCOUNT_CONFIRMATION_SUCCESS, { defaultValue: 'Your account confirmation was successful. You are now logged in.' });
      return { success: successMessage };
    } catch {
      return { error: t(LOCALE_FORM_ERROR_PATHS.ACCOUNT_CONFIRMATION_FAILED, { defaultValue: 'Your account confirmation was not successful.' }) };
    }
  };

  const signOut: IUserContext['signOut'] = async () => {
    const [, error] = await withTryAsync(endpoint)('/services/api/v3/users/logout', HttpRequestMethodTypes.DELETE);
    if (error) {
      console.error('An error occurred while signing out:', error);
    }
    saveAndPersistUser(null); // Set user to null after to allow for the jwt to be included in the logout request
  };

  const recoverPasswordForEmail: IUserContext['recoverPasswordForEmail'] = async email => {
    const [response, error] = await withTryAsync<object, ErrorResponse>(endpoint)('/services/api/v3/users/password', HttpRequestMethodTypes.POST, { email, theme: themeName });
    if (error || 'error' in response) {
      return { error: t(LOCALE_FORM_ERROR_PATHS.INVALID_EMAIL, { defaultValue: 'Invalid Email.' }) };
    } else {
      const successMessage = t(LOCALE_FORM_ERROR_PATHS.PASSWORD_RESET_EMAIL_SENT, { defaultValue: 'A message has been sent to reset your password. Please check your inbox.' });
      return { success: successMessage };
    }
  };

  const resetPassword: IUserContext['resetPassword'] = async params => {
    const [response, error] = await withTryAsync<object, ErrorResponse>(endpoint)('/services/api/v3/users/password', HttpRequestMethodTypes.PATCH, { ...params, theme: themeName });
    if (error || 'error' in response) {
      return { error: t(LOCALE_FORM_ERROR_PATHS.RESET_TOKEN_INVALID, { defaultValue: 'Reset Password Token is invalid.' }) };
    } else {
      const successMessage = t(LOCALE_FORM_ERROR_PATHS.PASSWORD_CHANGED_SUCCESS, { defaultValue: 'Your password was changed successfully.' });
      return { success: successMessage };
    }
  };

  const updatePassword: IUserContext['updatePassword'] = async params => {
    const [response, error] = await withTryAsync<JSONAPIPayload, ErrorResponse>(endpoint)('/services/api/v3/users/password', HttpRequestMethodTypes.PATCH, { ...params, theme: themeName });
    if (error) {
      const parsedError = parseError(error.errors?.[0]);
      return { error: parsedError };
    } else if ('error' in response) {
      return { error: t(LOCALE_FORM_ERROR_PATHS.GENERAL_ERROR, { defaultValue: 'Something went wrong. Please try again later.' }) };
    } else {
      if (response.data) {
        setUserFromResponse(response);
      }
      const successMessage = t(LOCALE_FORM_ERROR_PATHS.PASSWORD_CHANGED_SUCCESS, { defaultValue: 'Your password was changed successfully.' });
      return { success: successMessage };
    }
  };

  const updateUserData: IUserContext['updateUserData'] = async params => {
    const [response, error] = await withTryAsync<JSONAPIPayload, ErrorResponse>(endpoint)('/services/api/v3/users/${params.id}', HttpRequestMethodTypes.PATCH, { data: { attributes: dasherizeKeys(params) }});
    if (error) {
      return { error: t(LOCALE_FORM_ERROR_PATHS.IS_INVALID, { defaultValue: 'is invalid' }) };
    } else if ('error' in response) {
      return { error: t(LOCALE_FORM_ERROR_PATHS.IS_INVALID, { defaultValue: 'is invalid' }) };
    } else {
      saveAndPersistUser({ id: params.id, attributes: { ...params }});
      const successMessage = t(LOCALE_FORM_ERROR_PATHS.INFO_CHANGED_SUCCESS, { defaultValue: 'Your info was changed successfully.' });
      return { success: successMessage };
    }
  };

  const updateEmail: IUserContext['updateEmail'] = async params => {
    const data = {
      data: {
        type: 'users',
        attributes: {
          currentEmail: user?.email,
          email: params.newEmail,
          emailConfirmation: params.retypeEmail,
          password: params.password,
          passwordConfirmation: params.passwordConfirmation,
        },
      },
    };

    const [response, error] = await withTryAsync<JSONAPIPayload, ErrorResponse>(endpoint)('/services/api/v3/users/email', HttpRequestMethodTypes.PATCH, data);
    if (error) {
      const emailError = parseError(error.errors?.[0]);
      return { error: emailError };
    } else if ('error' in response) {
      return { error: t(LOCALE_FORM_ERROR_PATHS.IS_INVALID, { defaultValue: 'is invalid' }) };
    } else {
      if (user) saveAndPersistUser({ id: user.id, attributes: { ...user, email: params.newEmail, hasPassword: true }});
      const successMessage = t(LOCALE_FORM_ERROR_PATHS.EMAIL_CHANGED_SUCCESS, { defaultValue: 'Your email was changed successfully.' });
      return { success: successMessage };
    }
  };

  const setUserFromResponse = (response: JSONAPIPayload) => {
    return saveAndPersistUser(response.data);
  };

  const saveAndPersistUser = (user: JSONAPIPayload['data'] | null) => {
    if (user) {
      const newUser: User = { id: user.id, ...user.attributes };
      Cookies.set('user', JSON.stringify(newUser), { expires: USER_EXPIRE_DAYS });
      setUser(newUser);
    } else {
      Cookies.remove('user');
      setUser(null);
    }
  };

  const handleSetSiteLocation = (location: CountryCode) => {
    Cookies.set(SITE_LOCATION_COOKIE_NAME, location, { expires: LOCATION_EXPIRE_DAYS });
    setSiteLocation(location);
  };

  const handleSetDefaultUserLocation = (country: CountryCode) => {
    let location = defaultCACityPayload;
    if (country === CountryCodeList.UNITED_STATES) {
      location = defaultUSCityPayload;
    }
    Cookies.set(USER_LOCATION_COOKIE_NAME, JSON.stringify(location), { expires: DEFAULT_EXPIRE_DAYS });
    setUserLocation(location);
  };

  const handleConnRequestDisclaimers = ({ isChecked }: { isChecked: boolean }) => {
    if (isChecked) {
      Cookies.set('connection-request-disclaimers', 'true', { expires: USER_EXPIRE_DAYS });
      setHasAcceptedConnRequestDisclaimers(true);
    } else {
      Cookies.remove('connection-request-disclaimers');
      setHasAcceptedConnRequestDisclaimers(false);
    }
  };

  const parseError = (error: any) => {
    if (error && error.source && error.source.pointer && error.title) {
      const pointer = error.source.pointer.split('/').pop();
      const capitalizedPointer = pointer.charAt(0).toUpperCase() + pointer.slice(1);
      const title = error.title;
      return `${capitalizedPointer.replace(/-/g, ' ')} ${title}`;
    } else {
      return t(LOCALE_FORM_ERROR_PATHS.GENERAL_ERROR, { defaultValue: 'Something went wrong. Please try again later.' });
    }
  };

  return (
    <UserContext.Provider
      value={{
        isAuthenticated,
        user,
        authenticateUser,
        registerUser,
        acceptTerms,
        acceptedBrowserBasedTerms,
        acceptedTerms,
        setZoocasaTermsStateToRead,
        areZoocasaTermsRead,
        confirmUser,
        validateUser,
        signOut,
        recoverPasswordForEmail,
        resetPassword,
        saveAndPersistUser,
        userLocation,
        siteLocation,
        userLocationSameAsSiteLocation,
        handleSetSiteLocation,
        handleSetDefaultUserLocation,
        hasAcceptedConnRequestDisclaimers,
        handleConnRequestDisclaimers,
        userPopularCities,
        hasNavigatedFromInternalLink,
        setHasNavigatedFromInternalLink,
        updateUserData,
        updateEmail,
        updatePassword,
        isOnSignUpUrl,
        setIsOnSignUpUrl,
        isCrawler,
        functionalCookiesEnabled,
        aiSearchParams,
        setAiSearchParams,
      }}>
      {children}
    </UserContext.Provider>
  );
}

function useUser(): IUserContext {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserContextProvider');
  }
  return context as Required<IUserContext>;
}

function getUserFromRequest(request: IncomingMessage): User | null {
  return getObjectFromRequestCookie<User>(USER_COOKIE_NAME, request) || null;
}

function isUserAuthenticatedFromRequest(request: IncomingMessage): boolean {
  const user = getUserFromRequest(request);
  if (!user || user?.jwt?.length === 0) {
    return false;
  }
  const jwtCookie = getStringFromRequestCookie(JWT_COOKIE_NAME, request);
  if (!jwtCookie) {
    return false;
  }
  const decodedUserJwt = jwt.decode(user.jwt, { complete: true, json: true });
  const decodedJwtCookie = jwt.decode(jwtCookie, { complete: true, json: true });
  if (decodedUserJwt && decodedUserJwt.payload?.['user_id'] && decodedJwtCookie && decodedJwtCookie.payload?.['user_id']) {
    const userJwt_user_id = decodedUserJwt.payload?.['user_id'];
    const jwtCookie_user_id = decodedJwtCookie.payload?.['user_id'];
    return String(user.id) === String(userJwt_user_id) && String(user.id) === String(jwtCookie_user_id);
  }
  return false;
}

function getUserFromCookies(): User | null {
  const cookie = Cookies.get(USER_COOKIE_NAME);
  if (cookie) {
    return getJSON<User>(cookie);
  }
  return undefined;
}

// Helper function to validate JWT token format (for API routes)
function isValidJWTToken(token: string): boolean {
  try {
    if (!token || token.length === 0) {
      return false;
    }
    
    const decoded = jwt.decode(token, { complete: true, json: true });
    if (!decoded || !decoded.payload) {
      return false;
    }
    
    // Check if token has required fields
    const payload = decoded.payload as any;
    if (!payload.user_id) {
      return false;
    }
    
    // Check if token is expired (if exp field exists)
    if (payload.exp && payload.exp < Math.floor(Date.now() / 1000)) {
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('JWT validation error:', error);
    return false;
  }
}

export { useUser, getUserFromRequest, getUserFromCookies, isUserAuthenticatedFromRequest, isValidJWTToken };